/**
 * The chat's two reads and the one place their keys are spelled.
 *
 * ── `lang` is in the thread's key ────────────────────────────────────────────────────────────
 * The recording cards name an exercise resolved in the READER's language (exercise-clip-timeline
 * R20), so the thread is read with `lang` and the two languages' copies split rather than
 * overwrite each other. The inbox carries no exercise name and has no `lang`.
 *
 * ── The thread pages backwards ───────────────────────────────────────────────────────────────
 * The server answers newest first, 30 at a time; `before` is the oldest id of the previous page.
 * `pages[0]` is therefore the NEWEST page, which is where a live message is written.
 */

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import type { ContentLanguage } from '~/shared/i18n/contentLanguage';
import {
  getChatConversations,
  getChatConversationsConversationIdMessages,
} from '~/shared/api/generated/endpoints/chat/chat';

export const MESSAGE_PAGE_SIZE = 30;

export const chatRoot = ['chat'] as const;
export const conversationsQueryKey = [...chatRoot, 'conversations'] as const;
export const threadRoot = (conversationId: number) => [...chatRoot, 'thread', conversationId] as const;
export const threadQueryKey = (conversationId: number, lang: ContentLanguage) =>
  [...threadRoot(conversationId), lang] as const;

export function conversationsQueryOptions() {
  return queryOptions({
    queryKey: conversationsQueryKey,
    queryFn: ({ signal }) => getChatConversations(undefined, signal),
    staleTime: 30_000,
  });
}

export function threadQueryOptions(conversationId: number, lang: ContentLanguage) {
  return infiniteQueryOptions({
    queryKey: threadQueryKey(conversationId, lang),
    queryFn: ({ pageParam, signal }) =>
      getChatConversationsConversationIdMessages(
        conversationId,
        { limit: MESSAGE_PAGE_SIZE, lang, ...(pageParam === null ? {} : { before: pageParam }) },
        undefined,
        signal,
      ),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => (last.hasMore === true ? (last.nextCursor ?? null) : null),
    // The socket keeps the thread live; a refetch on every focus would only re-read what it said.
    staleTime: Infinity,
  });
}
